import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Spinner, Alert, Form } from "react-bootstrap";
import Editor from "@monaco-editor/react";
import {
  FaArrowLeft, FaCheckCircle, FaTimesCircle, FaPlay, FaTerminal,
  FaCloudUploadAlt, FaAngleDown, FaChevronUp, FaChevronDown,
  FaLightbulb, FaListOl, FaCode, FaKeyboard
} from "react-icons/fa";
import CommonToaster from "../Common/CommonToaster";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";
import { fetchJudgeLanguages, toSandboxLanguages } from "../../utils/judgeLanguages";
import "./practice.scss";

/* ── Bottom-panel tabs ── */
const TABS = [
  { key: "input", label: "Input", icon: <FaKeyboard /> },
  { key: "output", label: "Output", icon: <FaTerminal /> },
  { key: "results", label: "Test Results", icon: <FaListOl /> },
];

const PracticeCodingPage = () => {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const studentId = currentUser?.studentId;
  const editorRef = useRef(null);

  /* ── state ── */
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [languages, setLanguages] = useState([]);
  const [lang, setLang] = useState("");
  const [code, setCode] = useState("");
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [results, setResults] = useState([]);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [activeTab, setActiveTab] = useState("input");
  const [bottomOpen, setBottomOpen] = useState(true);
  const [leftWidth, setLeftWidth] = useState(38);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  /* ── fetch languages ── */
  useEffect(() => {
    fetchJudgeLanguages().then(raw => setLanguages(toSandboxLanguages(raw)));
  }, []);

  /* ── fetch question ── */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get(`/practice/questions/${questionId}`);
        setQuestion(data.question);
      } catch {
        setQuestion(null);
        setToast({ show: true, message: "Failed to load question", type: "error" });
      }
      setLoading(false);
    })();
  }, [questionId]);

  // Set initial language/code once both question and languages are available
  useEffect(() => {
    if (!question || languages.length === 0 || lang) return;
    const available = languages.find(l => question.sampleCode?.[l.codeKey]);
    if (available) {
      setLang(available.value);
      setCode(question.sampleCode[available.codeKey]);
    } else if (languages[0]) {
      setLang(languages[0].value);
    }
  }, [question, languages]); // eslint-disable-line

  /* ── language switch ── */
  const handleLangChange = (e) => {
    const v = e.target.value;
    setLang(v);
    if (question?.sampleCode) {
      const langDef = languages.find(l => l.value === v);
      setCode(question.sampleCode[langDef?.codeKey] || "");
    }
    setResults([]);
    setOutput("");
  };

  /* ── run single ── */
  const handleRun = async () => {
    setRunning(true);
    setOutput("");
    setActiveTab("output");
    setBottomOpen(true);
    const langDef = languages.find((l) => l.value === lang);
    const categoryMatch = question?.categories?.find(c => (typeof c === 'object' ? c.name : c) === langDef?.codeKey);
    const langId = (typeof categoryMatch === 'object' && categoryMatch?.id) || langDef?.id;
    if (!langId) { setRunning(false); return; }
    try {
      const { data } = await apiClient.post("/code/run", {
        source_code: code,
        language_id: langId,
        stdin: input,
      });
      setOutput((data.stdout || data.stderr || data.compile_output || "").trim());
    } catch {
      setOutput("Error running code.");
    }
    setRunning(false);
  };

  /* ── run all test cases ── */
  const handleRunAll = async () => {
    if (!question?.testCases?.length) return;
    setRunning(true);
    setResults([]);
    setActiveTab("results");
    setBottomOpen(true);
    const langDef = languages.find((l) => l.value === lang);
    const categoryMatch = question?.categories?.find(c => (typeof c === 'object' ? c.name : c) === langDef?.codeKey);
    const langId = (typeof categoryMatch === 'object' && categoryMatch?.id) || langDef?.id;
    if (!langId) { setRunning(false); return; }
    const tcs = (question.testCases || []).map((tc) => ({
      input: tc.input,
      expected: tc.expectedOutput,
    }));
    try {
      const { data } = await apiClient.post("/code/run-batch", {
        source_code: code,
        language_id: langId,
        testCases: tcs,
      });
      const allTcs = question.testCases || [];
      const taggedResults = (data.results || []).map((r, i) => ({ ...r, isHidden: !!allTcs[i]?.isHidden }));
      setResults(taggedResults);
    } catch {
      setToast({ show: true, message: "Failed to run test cases", type: "error" });
    }
    setRunning(false);
  };

  /* ── submit ── */
  const handleSubmit = async () => {
    if (!results.length) {
      setToast({ show: true, message: "Run all test cases first", type: "warning" });
      return;
    }
    setSaving(true);
    try {
      const { data } = await apiClient.post("/practice/submit", {
        studentId,
        questionId,
        language: lang,
        code,
        results,
      });
      const attempt = data.attempt;
      setLastResult(attempt);
      const scoreVal = attempt.score ?? (attempt.totalCount > 0 ? Math.round((attempt.passedCount / attempt.totalCount) * 100) : 0);
      const msg =
        attempt.status === "solved"
          ? `All test cases passed! Score: ${scoreVal}/100 🎉`
          : attempt.status === "partially_solved"
          ? `${attempt.passedCount}/${attempt.totalCount} passed · Score: ${scoreVal}/100`
          : `Submitted — Score: ${scoreVal}/100. Keep trying!`;
      setToast({ show: true, message: msg, type: attempt.status === "solved" ? "success" : "info" });
    } catch {
      setToast({ show: true, message: "Failed to save attempt", type: "error" });
    }
    setSaving(false);
  };

  /* ── drag to resize left/right ── */
  const onDragStart = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = leftWidth;
    const container = e.target.closest(".hr-split-container");
    if (!container) return;
    const totalW = container.getBoundingClientRect().width;

    const onMove = (ev) => {
      const delta = ev.clientX - startX;
      const pct = startW + (delta / totalW) * 100;
      setLeftWidth(Math.min(60, Math.max(22, pct)));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [leftWidth]);

  /* ── computed ── */
  const testCases = (question?.testCases || [])
    .filter((tc) => !tc.isHidden)
    .map((tc) => ({
      input: tc.input,
      expected: tc.expectedOutput,
    }));
  const passedCount = results.filter((r) => r.passed).length;
  const recommendedLangs = languages.filter(l => question?.sampleCode?.[l.codeKey]);
  const otherLangs = languages.filter(l => !question?.sampleCode?.[l.codeKey]);

  /* ── loading / error ── */
  if (loading) return <div className="text-center py-5"><Spinner animation="border" /></div>;
  if (!question) return <Alert variant="danger" className="mt-4 mx-3">Question not found.</Alert>;

  return (
    <div className="hr-practice-page">
      {/* ── Top Bar ── */}
      <div className="hr-topbar">
        <button className="hr-back-btn" onClick={() => navigate("/s/practice")}>
          <FaArrowLeft size={13} />
        </button>
        <span className="hr-topbar-title">{question.title}</span>
        <span className={`hr-diff-chip ${(question.difficulty || "").toLowerCase()}`}>
          {question.difficulty}
        </span>
        {lastResult && (
          <span className={`hr-result-chip ${lastResult.status}`}>
            {lastResult.status === "solved" ? <FaCheckCircle /> : null}
            <span className="hr-result-score">{lastResult.score ?? (lastResult.totalCount > 0 ? Math.round((lastResult.passedCount / lastResult.totalCount) * 100) : 0)}/100</span>
            <span className="hr-result-tc">{lastResult.passedCount}/{lastResult.totalCount} passed</span>
          </span>
        )}
        <div className="ms-auto" />
      </div>

      {/* ── Split Container ── */}
      <div className="hr-split-container">
        {/* LEFT — Question Panel */}
        <div className="hr-left-panel" style={{ width: `${leftWidth}%` }}>
          <div className="hr-panel-tabs">
            <span className="hr-panel-tab active"><FaLightbulb size={11} /> Problem</span>
          </div>
          <div className="hr-question-body">
            <h5 className="hr-q-title">{question.title}</h5>
            <div className="hr-q-meta">
              <span className={`hr-diff-badge ${(question.difficulty || "").toLowerCase()}`}>
                {question.difficulty}
              </span>
              {(question.categories || []).map((c) => (
                <span key={typeof c === 'object' ? c.name : c} className="hr-tag">{typeof c === 'object' ? c.name : c}</span>
              ))}
              {(question.labels || []).map((l) => (
                <span key={l} className="hr-tag label">{l}</span>
              ))}
            </div>
            <div className="hr-q-desc" dangerouslySetInnerHTML={{ __html: question.description }} />

            {/* Test Cases */}
            {testCases.length > 0 && (
              <div className="hr-tc-section">
                <div className="hr-tc-heading">
                  <FaListOl size={12} /> Sample Test Cases
                </div>
                {testCases.map((tc, i) => (
                  <div key={i} className="hr-tc-card">
                    <div className="hr-tc-row">
                      <span className="hr-tc-label">Input</span>
                      <pre className="hr-tc-pre">{tc.input || "(empty)"}</pre>
                    </div>
                    <div className="hr-tc-row">
                      <span className="hr-tc-label">Expected Output</span>
                      <pre className="hr-tc-pre">{tc.expected}</pre>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* DIVIDER */}
        <div className="hr-divider" onMouseDown={onDragStart}>
          <div className="hr-divider-grip" />
        </div>

        {/* RIGHT — Editor + I/O */}
        <div className="hr-right-panel" style={{ width: `${100 - leftWidth}%` }}>
          {/* Editor Toolbar */}
          <div className="hr-editor-toolbar">
            <div className="hr-lang-select-wrap">
              <Form.Select
                size="sm"
                value={lang}
                onChange={handleLangChange}
                className="hr-lang-select"
              >
                {recommendedLangs.length > 0 && (
                  <optgroup label="⭐ Recommended">
                    {recommendedLangs.map((l) => (
                      <option key={l.id} value={l.value}>{l.label}</option>
                    ))}
                  </optgroup>
                )}
                <optgroup label="Other Languages">
                  {otherLangs.map((l) => (
                    <option key={l.id} value={l.value}>{l.label}</option>
                  ))}
                </optgroup>
              </Form.Select>
              <FaAngleDown className="hr-lang-chevron" />
            </div>
            <div className="hr-toolbar-actions">
              <button className="hr-action-btn run" onClick={handleRun} disabled={running}>
                {running ? <Spinner size="sm" animation="border" /> : <><FaPlay size={10} /> Run</>}
              </button>
              <button className="hr-action-btn test" onClick={handleRunAll} disabled={running}>
                <FaTerminal size={10} /> Run Tests
              </button>
              <button
                className="hr-action-btn submit"
                onClick={handleSubmit}
                disabled={saving || !results.length}
              >
                <FaCloudUploadAlt size={12} /> Submit
              </button>
            </div>
          </div>

          {/* Monaco Editor */}
          <div className="hr-editor-area" style={{ flex: bottomOpen ? "1 1 60%" : "1 1 100%" }}>
            <Editor
              height="100%"
              language={languages.find((l) => l.value === lang)?.monacoId || "javascript"}
              value={code}
              onChange={(v) => setCode(v || "")}
              theme="vs-dark"
              onMount={(editor) => { editorRef.current = editor; editor.focus(); }}
              options={{
                fontSize: 14,
                fontFamily: "'Fira Code', 'Consolas', 'Courier New', monospace",
                minimap: { enabled: false },
                wordWrap: "on",
                scrollBeyondLastLine: false,
                padding: { top: 12 },
                formatOnType: true,
                contextmenu: false,
                lineNumbers: "on",
                renderLineHighlight: "line",
                bracketPairColorization: { enabled: true },
              }}
            />
          </div>

          {/* Bottom I/O Panel */}
          <div className={`hr-bottom-panel ${bottomOpen ? "open" : "collapsed"}`}>
            <div className="hr-bottom-header" onClick={() => setBottomOpen((p) => !p)}>
              <div className="hr-bottom-tabs">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    className={`hr-bottom-tab ${activeTab === t.key ? "active" : ""}`}
                    onClick={(e) => { e.stopPropagation(); setActiveTab(t.key); setBottomOpen(true); }}
                  >
                    {t.icon} {t.label}
                    {t.key === "results" && results.length > 0 && (
                      <span className={`hr-tab-badge ${passedCount === results.length ? "pass" : "mixed"}`}>
                        {passedCount}/{results.length}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <button className="hr-collapse-btn">
                {bottomOpen ? <FaChevronDown size={11} /> : <FaChevronUp size={11} />}
              </button>
            </div>
            {bottomOpen && (
              <div className="hr-bottom-body">
                {activeTab === "input" && (
                  <textarea
                    className="hr-io-textarea"
                    placeholder="Enter custom input here…"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    spellCheck={false}
                  />
                )}
                {activeTab === "output" && (
                  <pre className="hr-io-output">{output || "Run your code to see output"}</pre>
                )}
                {activeTab === "results" && (
                  <div className="hr-results-list">
                    {results.length === 0 ? (
                      <div className="hr-results-empty">
                        <FaCode size={20} />
                        <span>Run test cases to see results</span>
                      </div>
                    ) : (
                      <>
                      <div className="hr-results-summary">
                        <span className={`hr-results-score ${passedCount === results.length ? "hr-results-score--perfect" : passedCount > 0 ? "hr-results-score--partial" : "hr-results-score--zero"}`}>
                          Score: {results.length > 0 ? Math.round((passedCount / results.length) * 100) : 0}/100
                        </span>
                        <span className="hr-results-tc-count">{passedCount}/{results.length} test cases passed</span>
                      </div>
                      {results.filter(r => !r.isHidden).map((r, i) => (
                        <div key={i} className={`hr-result-row ${r.passed ? "pass" : "fail"}`}>
                          <div className="hr-result-status">
                            {r.passed
                              ? <FaCheckCircle className="text-success" />
                              : <FaTimesCircle className="text-danger" />}
                            <span>Case {i + 1}</span>
                          </div>
                          <div className="hr-result-data">
                            <div><span className="hr-rd-label">Input</span><code>{r.input || "(empty)"}</code></div>
                            <div><span className="hr-rd-label">Expected</span><code>{r.expected}</code></div>
                            <div><span className="hr-rd-label">Output</span><code>{r.output || "—"}</code></div>
                          </div>
                        </div>
                      ))}
                      {(() => {
                        const hiddenResults = results.filter(r => r.isHidden);
                        if (!hiddenResults.length) return null;
                        const hiddenPassed = hiddenResults.filter(r => r.passed).length;
                        return (
                          <div className={`hr-result-row ${hiddenPassed === hiddenResults.length ? "pass" : "fail"}`} style={{ opacity: 0.8 }}>
                            <div className="hr-result-status">
                              {hiddenPassed === hiddenResults.length
                                ? <FaCheckCircle className="text-success" />
                                : <FaTimesCircle className="text-danger" />}
                              <span>{hiddenPassed}/{hiddenResults.length} hidden test case{hiddenResults.length !== 1 ? "s" : ""} passed</span>
                            </div>
                          </div>
                        );
                      })()}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default PracticeCodingPage;
